// frontend/src/pages/Injuries/Injuries.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Activity,
    Users,
    AlertTriangle,
    Clock,
    RefreshCw,
    Info,
    CheckCircle,
    Trophy,
    Loader2
} from 'lucide-react';
import { injuriesAPI } from '../services/api';
import { cleanShieldUrl } from '../utils/imageUtils';

const SEARCH_MODES = { TEAM: 'team', COMPETITION: 'competition' };

const Injuries = () => {
    const [teams, setTeams] = useState({});
    const [searchMode, setSearchMode] = useState(SEARCH_MODES.TEAM);

    // Modo por equipo (existente)
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedCompetition, setSelectedCompetition] = useState('ea-sports');
    const [results, setResults] = useState(null);

    // Modo por competición (nuevo)
    const [selectedCompetitionForAll, setSelectedCompetitionForAll] = useState('ea-sports');
    const [competitionResults, setCompetitionResults] = useState(null);
    const [competitionProgress, setCompetitionProgress] = useState({ current: 0, total: 0, teamName: '' });

    // Compartidos
    const [loading, setLoading] = useState(false);
    const [loadingTeams, setLoadingTeams] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadTeamsByCompetition();
    }, []);

    const loadTeamsByCompetition = async () => {
        try {
            setLoadingTeams(true);
            const response = await injuriesAPI.getTeamsByCompetition();
            if (response.data.success) {
                setTeams(response.data.data);
            } else {
                throw new Error(response.data.message || 'Error cargando equipos');
            }
        } catch (err) {
            setError('Error al cargar los equipos disponibles');
        } finally {
            setLoadingTeams(false);
        }
    };

    // --- MODO POR EQUIPO (existente, sin cambios) ---
    const analyzeInjuries = async () => {
        if (!selectedTeam) {
            setError('Por favor selecciona un equipo');
            return;
        }
        try {
            setLoading(true);
            setError('');
            setResults(null);
            const response = await injuriesAPI.analyzeTeam(selectedTeam, '2025');
            if (response.data.success) {
                setResults(response.data.data);
            } else {
                throw new Error(response.data.message || 'Error en el análisis');
            }
        } catch (err) {
            setError('Error al analizar las lesiones. Inténtalo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    // --- MODO POR COMPETICIÓN (nuevo) ---
    const analyzeCompetition = async () => {
        const competitionTeams = teams[selectedCompetitionForAll]?.teams || [];
        if (competitionTeams.length === 0) {
            setError('No hay equipos disponibles para esta competición');
            return;
        }

        setLoading(true);
        setError('');
        setCompetitionResults(null);
        setCompetitionProgress({ current: 0, total: competitionTeams.length, teamName: '' });

        const allTeamResults = [];

        for (let i = 0; i < competitionTeams.length; i++) {
            const team = competitionTeams[i];
            setCompetitionProgress({ current: i + 1, total: competitionTeams.length, teamName: team.name });

            try {
                const response = await injuriesAPI.analyzeTeam(team.id, '2025');
                if (response.data.success) {
                    const data = response.data.data;
                    allTeamResults.push({
                        teamId: team.id,
                        teamName: team.name,
                        teamShield: team.shield || '',
                        totalPlayers: data.team?.totalPlayers || 0,
                        injuredPlayers: data.team?.injuredPlayers || 0,
                        injuredList: data.team?.injuredList || [],
                        injuriesByType: data.injuriesByType || {},
                    });
                }
            } catch (err) {
                console.warn(`⚠️ Error analizando ${team.name}:`, err.message);
                allTeamResults.push({
                    teamId: team.id,
                    teamName: team.name,
                    teamShield: team.shield || '',
                    totalPlayers: 0,
                    injuredPlayers: 0,
                    injuredList: [],
                    injuriesByType: {},
                });
            }
        }

        const totalInjured = allTeamResults.reduce((sum, t) => sum + t.injuredPlayers, 0);
        setCompetitionResults({
            competitionName: getCompetitionName(selectedCompetitionForAll),
            teams: allTeamResults.sort((a, b) => b.injuredPlayers - a.injuredPlayers),
            totalInjured,
        });
        setLoading(false);
    };

    const getInjuryTypeColor = (type) => {
        const colors = {
            'Rodilla': 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
            'Tobillo': 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20',
            'Muslo': 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
            'Isquiotibiales': 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
            'Ingle': 'text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/20',
            'Gemelo': 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
            'Espalda': 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800',
            'Lesión muscular': 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
            'Ligamento': 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
            'Fractura': 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30',
            'ankle': 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20',
            'knee': 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
            'muscle': 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
            'hamstring': 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
            'back': 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800'
        };
        return colors[type] || 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800';
    };

    const getTeamsForSelectedCompetition = () => teams[selectedCompetition]?.teams || [];

    const getCompetitionName = (key) => {
        const names = {
            'ea-sports': 'LaLiga EA Sports',
            'hypermotion': 'LaLiga Hypermotion',
            'primera-rfef': 'Primera RFEF'
        };
        return names[key] || key;
    };

    const getTeamName = (teamId) => {
        const allTeams = Object.values(teams).flatMap(comp => comp.teams);
        const team = allTeams.find(t => t.id === teamId);
        return team?.name || `Equipo ${teamId}`;
    };

    if (loadingTeams) {
        return (
            <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 p-6">
                <div className="max-w-6xl mx-auto text-center py-12">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-4" />
                    <p className="text-secondary-600 dark:text-secondary-400">Cargando equipos disponibles...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 p-6">
            <div className="max-w-6xl mx-auto">

                {/* Header */}
                <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <Activity className="h-6 w-6 text-red-500 dark:text-red-400" />
                        <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">Análisis de Lesiones</h1>
                    </div>
                    <p className="text-secondary-600 dark:text-secondary-400 mb-5">
                        Analiza el estado de lesiones de un equipo o de una competición completa.
                    </p>

                    {/* Selector de modo */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => { setSearchMode(SEARCH_MODES.TEAM); setResults(null); setCompetitionResults(null); setError(''); }}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${searchMode === SEARCH_MODES.TEAM
                                    ? 'bg-red-500 text-white shadow-sm'
                                    : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
                                }`}
                        >
                            <Users className="h-4 w-4" />
                            Por equipo
                        </button>
                        <button
                            onClick={() => { setSearchMode(SEARCH_MODES.COMPETITION); setResults(null); setCompetitionResults(null); setError(''); }}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${searchMode === SEARCH_MODES.COMPETITION
                                    ? 'bg-red-500 text-white shadow-sm'
                                    : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-600'
                                }`}
                        >
                            <Trophy className="h-4 w-4" />
                            Por competición
                        </button>
                    </div>
                </div>

                {/* Panel búsqueda */}
                <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-6 mb-6">

                    {/* MODO: Por equipo (original) */}
                    {searchMode === SEARCH_MODES.TEAM && (
                        <>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Seleccionar Equipo</h2>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Competición</label>
                                <select
                                    value={selectedCompetition}
                                    onChange={(e) => { setSelectedCompetition(e.target.value); setSelectedTeam(''); setResults(null); }}
                                    className="w-full p-3 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {Object.keys(teams).map(key => (
                                        <option key={key} value={key}>
                                            {getCompetitionName(key)} ({teams[key]?.teams?.length || 0} equipos)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Seleccionar Equipo</label>
                                <select
                                    value={selectedTeam}
                                    onChange={(e) => setSelectedTeam(e.target.value)}
                                    className="w-full p-3 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Seleccionar equipo...</option>
                                    {getTeamsForSelectedCompetition().map(team => (
                                        <option key={team.id} value={team.id}>{team.name}</option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={analyzeInjuries}
                                disabled={!selectedTeam || loading}
                                className="w-full bg-red-500 hover:bg-red-600 disabled:bg-secondary-300 dark:disabled:bg-secondary-600 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                                {loading
                                    ? <><RefreshCw className="h-4 w-4 animate-spin" /> Analizando lesiones...</>
                                    : <><Activity className="h-4 w-4" /> Analizar Lesiones del Equipo</>
                                }
                            </button>
                        </>
                    )}

                    {/* MODO: Por competición (nuevo) */}
                    {searchMode === SEARCH_MODES.COMPETITION && (
                        <>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Seleccionar Competición</h2>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Competición</label>
                                <select
                                    value={selectedCompetitionForAll}
                                    onChange={(e) => { setSelectedCompetitionForAll(e.target.value); setCompetitionResults(null); }}
                                    className="w-full p-3 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    {Object.keys(teams).map(key => (
                                        <option key={key} value={key}>
                                            {getCompetitionName(key)} ({teams[key]?.teams?.length || 0} equipos)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                                <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-amber-800 dark:text-amber-200">
                                    Se analizarán todos los equipos de <strong>{getCompetitionName(selectedCompetitionForAll)}</strong> ({teams[selectedCompetitionForAll]?.teams?.length || 0} equipos). Puede tardar varios minutos.
                                </p>
                            </div>

                            <button
                                onClick={analyzeCompetition}
                                disabled={loading}
                                className="w-full bg-red-500 hover:bg-red-600 disabled:bg-secondary-300 dark:disabled:bg-secondary-600 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                                {loading
                                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Analizando competición...</>
                                    : <><Trophy className="h-4 w-4" /> Analizar Competición Completa</>
                                }
                            </button>
                        </>
                    )}

                    {error && (
                        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}
                </div>

                {/* Info box */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="text-blue-800 dark:text-blue-200 text-sm">
                            <p className="font-medium mb-1">Información del análisis:</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Solo se muestran lesiones activas de la temporada 2025</li>
                                <li>El proceso puede tardar 1-2 minutos por equipo</li>
                                <li>Se incluye tipo de lesión y fecha estimada de vuelta</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Progreso por competición */}
                <AnimatePresence>
                    {loading && searchMode === SEARCH_MODES.COMPETITION && competitionProgress.total > 0 && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-6 mb-6">
                            <div className="text-center">
                                <Loader2 className="h-10 w-10 animate-spin mx-auto text-red-500 mb-3" />
                                <p className="font-semibold text-secondary-900 dark:text-white mb-1">Analizando competición...</p>
                                <p className="text-sm text-secondary-500 dark:text-secondary-400 mb-4">{competitionProgress.teamName}</p>
                                <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-2 mb-2">
                                    <motion.div
                                        className="bg-red-500 h-2 rounded-full"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${Math.round((competitionProgress.current / competitionProgress.total) * 100)}%` }}
                                        transition={{ duration: 0.4 }}
                                    />
                                </div>
                                <p className="text-xs text-secondary-400">
                                    Equipo {competitionProgress.current} de {competitionProgress.total}
                                </p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ===================== RESULTADOS: MODO POR EQUIPO (original) ===================== */}
                {results && searchMode === SEARCH_MODES.TEAM && (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
                                Análisis de Lesiones - {getTeamName(results.team.id)}
                            </h2>
                            <div className="grid md:grid-cols-3 gap-6 mb-6">
                                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <Users className="h-8 w-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                                    <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{results.team.totalPlayers}</div>
                                    <div className="text-sm text-blue-700 dark:text-blue-300">Jugadores en Plantilla</div>
                                </div>
                                <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                    <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400 mx-auto mb-2" />
                                    <div className="text-2xl font-bold text-red-900 dark:text-red-100">{results.team.injuredPlayers}</div>
                                    <div className="text-sm text-red-700 dark:text-red-300">Jugadores Lesionados</div>
                                </div>
                                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                                    <div className="text-2xl font-bold text-green-900 dark:text-green-100">{results.team.totalPlayers - results.team.injuredPlayers}</div>
                                    <div className="text-sm text-green-700 dark:text-green-300">Jugadores Disponibles</div>
                                </div>
                            </div>
                            {Object.keys(results.injuriesByType).length > 0 && (
                                <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                                    <h3 className="font-medium text-secondary-900 dark:text-white mb-3">Lesiones por Tipo</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(results.injuriesByType).map(([type, count]) => (
                                            <span key={type} className={`px-3 py-1 rounded-full text-xs font-medium ${getInjuryTypeColor(type)}`}>
                                                {type}: {count}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Jugadores Lesionados</h3>
                            {results.team.injuredList.length > 0 ? (
                                <div className="space-y-4">
                                    {results.team.injuredList.map((player, index) => (
                                        <div key={index} className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h4 className="font-medium text-secondary-900 dark:text-white">{player.nick}</h4>
                                                    <p className="text-sm text-secondary-600 dark:text-secondary-400">{player.position}</p>
                                                </div>
                                                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded text-xs font-medium">LESIONADO</span>
                                            </div>
                                            {player.injuries.map((injury, injIndex) => (
                                                <div key={injIndex} className="mt-3 p-3 bg-secondary-50 dark:bg-secondary-700 rounded">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getInjuryTypeColor(injury.injured_name || injury.group_type_spanish || injury.group_type)}`}>
                                                            {injury.injured_name || injury.group_type_spanish || injury.group_type || 'Lesión'}
                                                        </span>
                                                        {injury.injured_start && (
                                                            <span className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1">
                                                                <Clock className="h-3 w-3" />
                                                                Desde: {injury.injured_start_formatted || injury.injured_start}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-secondary-700 dark:text-secondary-300">{injury.status_spanish}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                    <p>No hay jugadores lesionados actualmente</p>
                                </div>
                            )}
                        </div>

                        <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Información del Análisis</h3>
                            <div className="grid md:grid-cols-2 gap-6 text-sm text-secondary-600 dark:text-secondary-400">
                                <div>
                                    <p><span className="font-medium text-secondary-900 dark:text-white">Fecha del análisis:</span> {new Date(results.metadata.processedAt).toLocaleString()}</p>
                                    <p><span className="font-medium text-secondary-900 dark:text-white">Temporada:</span> {results.metadata.season}</p>
                                    <p><span className="font-medium text-secondary-900 dark:text-white">Fuente de datos:</span> {results.metadata.apiSource}</p>
                                </div>
                                <div>
                                    <p><span className="font-medium text-secondary-900 dark:text-white">Equipo analizado:</span></p>
                                    <ul className="list-disc list-inside mt-1 ml-4">
                                        <li>{getTeamName(results.metadata.searchParams.teamId)}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ===================== RESULTADOS: MODO POR COMPETICIÓN (nuevo) ===================== */}
                {competitionResults && searchMode === SEARCH_MODES.COMPETITION && !loading && (
                    <div className="space-y-4">
                        {/* Resumen */}
                        <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <Trophy className="h-5 w-5 text-red-500" />
                                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">{competitionResults.competitionName}</h3>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                    <p className="text-3xl font-bold text-red-700 dark:text-red-300">{competitionResults.totalInjured}</p>
                                    <p className="text-sm text-secondary-600 dark:text-secondary-400">Total lesionados</p>
                                </div>
                                <div className="text-center p-4 bg-secondary-50 dark:bg-secondary-700 rounded-lg">
                                    <p className="text-3xl font-bold text-secondary-700 dark:text-secondary-200">{competitionResults.teams.length}</p>
                                    <p className="text-sm text-secondary-600 dark:text-secondary-400">Equipos analizados</p>
                                </div>
                                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <p className="text-3xl font-bold text-green-700 dark:text-green-300">{competitionResults.teams.filter(t => t.injuredPlayers === 0).length}</p>
                                    <p className="text-sm text-secondary-600 dark:text-secondary-400">Sin lesionados</p>
                                </div>
                            </div>
                        </div>

                        {/* Un card por equipo ordenado por nº lesionados */}
                        {competitionResults.teams.map((team) => (
                            <div key={team.teamId} className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-5">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        {team.teamShield && (
                                            <img src={cleanShieldUrl(team.teamShield)} alt={team.teamName} className="w-8 h-8 object-contain"
                                                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                                        )}
                                        <h4 className="font-semibold text-secondary-900 dark:text-white">{team.teamName}</h4>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-secondary-500 dark:text-secondary-400">{team.totalPlayers} jugadores</span>
                                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${team.injuredPlayers === 0
                                                ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                                                : team.injuredPlayers >= 4
                                                    ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                                                    : 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                            }`}>
                                            {team.injuredPlayers} lesionado{team.injuredPlayers !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>

                                {team.injuredList.length > 0 ? (
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
                                        {team.injuredList.map((player, pIdx) => (
                                            <div key={pIdx} className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-800 rounded-lg">
                                                <p className="font-medium text-sm text-secondary-900 dark:text-white truncate">{player.nick}</p>
                                                <p className="text-xs text-secondary-500 dark:text-secondary-400">{player.position}</p>
                                                {player.injuries?.[0] && (
                                                    <span className={`inline-block mt-1 text-xs px-1.5 py-0.5 rounded ${getInjuryTypeColor(player.injuries[0].injured_name || player.injuries[0].group_type_spanish || player.injuries[0].group_type)}`}>
                                                        {player.injuries[0].injured_name || player.injuries[0].group_type_spanish || player.injuries[0].group_type || 'Lesión'}
                                                    </span>
                                                )}
                                                <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                                                    {player.injuries?.[0]?.status_spanish || player.injuryStatus}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                                        <CheckCircle className="h-4 w-4" /> Sin lesionados
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Injuries;